import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {PAGES_AND_PATHS} from '../../../../../constants/pages-and-paths';
import {ActivatedRoute, Router} from '@angular/router';
import {BlogsHttpService} from '../../../../../services/http-services/blogs/blogs-http.service';
import {debounceTime} from 'rxjs/operators';
import {ChunkUploadService} from '../../../../../services/helpers/chunk-upload-service.service';
import {BlogSectionType, BlogTypeEnum} from '../../../../../enums/blog-enums';


@Component({
  selector: 'app-edit-blogs',
  templateUrl: './edit-blog.component.html'
})
export class EditBlogComponent implements OnInit {
  formTriedToSubmit = false;
  editBlogForm!: FormGroup;
  urlParams: {blogId: number | null} = {
    blogId: null
  };
  coverImageUrl: any;
  blogCategories: any = [
    {name: 'Igraonice'},
    {name: 'Ketering'},
    {name: 'Torte'},
    {name: 'Animatori'},
    {name: 'Dekoracije'},
    {name: 'Fotografi'},
    {name: 'Pokloni'},
    {name: 'Dogadjaji'},
    {name: 'Ostalo'},
  ];

  addNewBlogElementDropdown = [
    {name: 'Naslov'},
    {name: 'Text'},
    {name: 'Slika'},
    {name: 'Video'}
  ];

  modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [{list: 'ordered'}, {list: 'bullet'}],
      [{indent: '-1'}, {indent: '+1'}],
      [{align: []}],
      ['link']
    ]
  };
  totalProgress = 0;
  isUploading = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  protected readonly PAGES_AND_PATHS = PAGES_AND_PATHS;
  protected readonly BlogSectionType = BlogSectionType;
  protected readonly BlogTypeEnum = BlogTypeEnum;

  constructor(private formBuilder: FormBuilder,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private toastService: ToastrService,
              private chunkUploadService: ChunkUploadService,
              private blogsHttpService: BlogsHttpService) {
  }

  ngOnInit(): void {
    this.buildForm();
    this.activatedRoute.params.subscribe(params => {
      if (params.blogId) {
        this.urlParams.blogId = params.blogId;
        this.getBlog();
      }
    });
  }

  buildForm(): void {
    this.editBlogForm = this.formBuilder.group({
      category: ['', Validators.required],
      author_name: ['', Validators.required],
      cover_image: ['', Validators.required],
      title: ['', Validators.required],
      intro_text: ['', Validators.required],
      sections: this.formBuilder.array([]),
      is_featured: [0, Validators.required],
      is_active: [0, Validators.required],
      is_saved: [0, Validators.required]
    });

    if (!this.editBlogForm.controls.is_saved.value) {
      this.editBlogForm.valueChanges.pipe(debounceTime(5000)).subscribe(values => {
        this.saveBlogDraftChanges();
      });
    }
  }

  get sections(): FormArray {
    return this.editBlogForm.get('sections') as FormArray;
  }

  getBlog(): void {
    this.blogsHttpService.getBlog(this.urlParams).then(res => {
      this.coverImageUrl = res.data.image?.full_path;
      this.editBlogForm.patchValue({
        ...res.data,
        cover_image: res.data.image?.name,
        sections: []
      });

      const sections = res.data.sections || [];
      this.sections.clear();
      sections.forEach((section: any) => {
        const sectionGroup: any = {
          uuid: [section.uuid, Validators.required],
          type: [section.type, Validators.required],
          value: [section.value || '', Validators.required]
        };

        if (section.type === BlogSectionType.image || section.type === BlogSectionType.video) {
          sectionGroup.fullPath = [section.media.full_url || ''];
        }

        this.sections.push(this.formBuilder.group(sectionGroup));
      });
    });
  }

  onChangeFeatured(event: any): void {
    this.editBlogForm.controls.is_featured.patchValue(event.target.checked ? 1 : 0);
  }

  onFileChange(event: any, sectionIndex?: number, isVideo = false, uuid = ''): void {
    this.isUploading = true;
    const files: FileList = event.target.files;
    const fileArray = Array.from(files);
    fileArray.forEach((file: File) => {
      let prevProgress = 0;
      if (!isVideo && file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/jpg' && file.type !== 'image/svg') {
        this.toastService.warning('Pogrešan tip dokumenta. Samo JPG, JPEG, SVG i PNG formati su dozvoljeni.');
        return;
      } else if (isVideo && file.type !== 'video/mp4' && file.type !== 'video/webm') {
        this.toastService.warning('Pogrešan tip dokumenta. Samo MP4 i WEBM formati su dozvoljeni.');
        return;
      } else if (file?.size > 100485760) {
        this.toastService.warning(`Slika ${file.name} je veća od 10MB.`);
        return;
      } else {
        const chunkSize = 5 * 1024 * 1024;  // 5MB per chunk
        const {blogId} = this.urlParams;
        this.chunkUploadService.uploadFile(file, blogId, chunkSize, uuid).subscribe({
          next: (data) => {
            this.totalProgress = (this.totalProgress - prevProgress + data.progress);
            prevProgress = data.progress;
            if (data.progress === 100) {
              (sectionIndex || sectionIndex === 0)
                ? this.editBlogForm.controls.sections.value[sectionIndex].value = data.fileName.path
                : this.editBlogForm.controls.cover_image.patchValue(data.fileName.filename);
            }
          },
          complete: () => {
            setTimeout( () => {
              this.isUploading = false;
              this.totalProgress = 0;
              if (sectionIndex || sectionIndex === 0) {
                this.editBlogForm.controls.sections.value[sectionIndex].fullPath = URL.createObjectURL(file);
              } else {
                this.coverImageUrl = URL.createObjectURL(file);
              }
              this.saveBlogDraftChanges();
            }, 400);
          },
          error: (err) => {
            console.error('Upload error:', err);
          }
        });
        this.fileInput.nativeElement.value = '';
      }
    });
  }

  removeCoverImage(): void {
    this.coverImageUrl = '';
    this.editBlogForm.controls.cover_image.patchValue('');
  }

  addNewSection(type: BlogSectionType): void {
    const newSection = this.formBuilder.group({
      type: [type],
      value: ['', Validators.required],
      uuid: crypto.randomUUID()
    });
    this.sections.push(newSection);
  }

  removeSection(sectionIndex: number): void {
    this.sections.removeAt(sectionIndex);
  }

  removeSectionFile(sectionIndex: number): void {
    this.sections.value[sectionIndex].value = null;
    this.sections.value[sectionIndex].fullPath = null;
  }

  saveBlogDraftChanges(): void {
    const requestBody = {...this.editBlogForm.value, sections: this.editBlogForm.controls.sections.value};
    this.blogsHttpService.putBlog(this.urlParams, requestBody).then(() => {

    });
  }

  submitEditBlogForm(): void {
    this.formTriedToSubmit = true;
    if (this.editBlogForm.valid) {
      this.editBlogForm.controls.is_saved.patchValue(true);
      this.blogsHttpService.putBlog(this.urlParams, this.editBlogForm.value).then(res => {
        this.router.navigate([PAGES_AND_PATHS.adminBlogs.pagePath], {
          state: {
            blogType: this.editBlogForm.controls.is_saved.value
              ? this.editBlogForm.controls.is_active.value
                ? BlogTypeEnum.active
                : BlogTypeEnum.inactive
              : BlogTypeEnum.draft
          },
        });
      }).catch(({error}: { error: { errors: { [key: string]: string } } }) => {
        Object.values(error.errors).forEach(error => this.toastService.error(error));
      });
    }
  }
}
